import { z } from 'zod';

const uuid = z.string().uuid();

/**
 * A time of day as `HH:MM` (24 h).
 */
const clock = z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/, 'HH:MM');

/**
 * Weekday of a block: 1 (lunes) to 6 (sábado).
 */
const weekday = z.number().int().min(1).max(6);

/**
 * Body of `POST /school/timetable/blocks`: one block of a group's week (scoping 3.6). The group
 * fixes the school year and the sede; teacher and classroom may be named later.
 */
export const createTimetableBlockInputSchema = z
  .object({
    groupId: uuid,
    subjectId: uuid,
    teacherId: uuid.nullable().optional(),
    classroomId: uuid.nullable().optional(),
    weekday,
    startsAt: clock,
    endsAt: clock,
  })
  .refine((block) => block.startsAt < block.endsAt, {
    path: ['endsAt'],
    message: 'endsAt must be after startsAt',
  });

/**
 * Body of `PATCH /school/timetable/blocks/{id}`: any field of the block; the merged block is
 * validated and checked for collisions like a new one.
 */
export const updateTimetableBlockInputSchema = z
  .object({
    groupId: uuid,
    subjectId: uuid,
    teacherId: uuid.nullable(),
    classroomId: uuid.nullable(),
    weekday,
    startsAt: clock,
    endsAt: clock,
  })
  .partial();

/**
 * Body of `POST /school/timetable/copy`: the week of one group (last year's, or a parallel
 * group's) placed on another; `replace` removes the target's blocks first.
 */
export const copyTimetableInputSchema = z
  .object({
    fromGroupId: uuid,
    toGroupId: uuid,
    replace: z.boolean().optional(),
  })
  .refine((input) => input.fromGroupId !== input.toGroupId, {
    path: ['toGroupId'],
    message: 'The target must be another group',
  });

/**
 * Query of `GET /school/timetable`: exactly one of a group, a teacher, a student (the group the
 * student is enrolled in this year) or a classroom (its occupancy, #113); the teacher and
 * classroom views are for the current year unless `schoolYearId` says otherwise.
 */
export const timetableQuerySchema = z
  .object({
    groupId: uuid.optional(),
    teacherId: uuid.optional(),
    studentId: uuid.optional(),
    classroomId: uuid.optional(),
    schoolYearId: uuid.optional(),
  })
  .refine(
    (query) =>
      [query.groupId, query.teacherId, query.studentId, query.classroomId].filter(Boolean)
        .length === 1,
    {
      path: ['groupId'],
      message: 'Give exactly one of groupId, teacherId, studentId or classroomId',
    },
  );

/**
 * One block of a weekly timetable (weekday 1 = Monday), with the latest class-log entry of its
 * group and subject so a teacher sees where the class is.
 */
export type TimetableBlock = {
  id: string;
  weekday: number;
  startsAt: string;
  endsAt: string;
  groupId: string;
  groupName: string;
  subjectId: string;
  subjectName: string;
  teacherId: string | null;
  teacherName: string | null;
  classroomId: string | null;
  classroomName: string | null;
  lastClassLog: { date: string; topicTitle: string | null } | null;
};

/**
 * A weekly timetable: a group's (`groupId` set), a teacher's (`teacherId` set) or a classroom's
 * occupancy (`classroomId` set).
 */
export type TimetableView = {
  groupId: string | null;
  groupName: string | null;
  teacherId: string | null;
  teacherName: string | null;
  classroomId: string | null;
  classroomName: string | null;
  schoolYearId: string | null;
  blocks: TimetableBlock[];
};

/**
 * What a colliding block takes that the new one also needs.
 */
export type TimetableConflictKind = 'group' | 'teacher' | 'classroom';

/**
 * A block a write collides with, carried in the 409's `error.details.conflicts`.
 */
export type TimetableConflict = {
  kind: TimetableConflictKind;
  block: Omit<TimetableBlock, 'lastClassLog'>;
};

/**
 * Answer of `POST /school/timetable/copy`: the target's new week, the copied blocks whose
 * teacher or classroom was left empty because it was busy at that time in another group, and
 * the source blocks not copied because they overlapped one already copied.
 */
export type TimetableCopyResult = {
  view: TimetableView;
  copied: number;
  cleared: { blockId: string; field: 'teacher' | 'classroom'; conflictBlockId: string }[];
  skipped: string[];
};

/**
 * Body of `POST /school/timetable/blocks`.
 */
export type CreateTimetableBlockInput = z.input<typeof createTimetableBlockInputSchema>;

/**
 * Body of `PATCH /school/timetable/blocks/{id}`.
 */
export type UpdateTimetableBlockInput = z.input<typeof updateTimetableBlockInputSchema>;

/**
 * Body of `POST /school/timetable/copy`.
 */
export type CopyTimetableInput = z.input<typeof copyTimetableInputSchema>;

/**
 * Query of `GET /school/timetable`.
 */
export type TimetableQuery = z.input<typeof timetableQuerySchema>;
